import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { RefreshCcw, CircleAlert, CircleCheck, CircleDashed } from "lucide-react";

/**
 * MYOB Sync Health strip — 4 compact chips showing per-collection freshness:
 *   • Chart of Accounts      (myob_accounts)
 *   • P&L Actuals            (myob_pl_actuals)
 *   • Aged Receivables       (myob_aged_receivables — via Report/AgedReceivablesSummary)
 *   • Open Invoices          (myob_invoices where amount_due > 0 — fallback path)
 *
 * Colours:
 *   green  → last successful sync < 4 h ago
 *   amber  → 4-48 h ago, OR last run errored but a recent success exists
 *   red    → no successful sync recorded, or > 48 h stale
 *
 * Backed by `/api/myob/sync/health`.
 */
export default function MYOBSyncHealthStrip({ compact = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/myob/sync/health");
      setData(data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Failed to load sync health");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading && !data) {
    return (
      <div className="flex gap-2 py-3" data-testid="myob-health-loading">
        <div className="h-9 w-40 shimmer rounded" />
        <div className="h-9 w-40 shimmer rounded" />
        <div className="h-9 w-40 shimmer rounded" />
        <div className="h-9 w-40 shimmer rounded" />
      </div>
    );
  }
  if (error) return null;
  if (!data) return null;

  const overallDot = STATUS_COLOR[data.overall_status] || STATUS_COLOR.red;

  return (
    <div
      className="border border-slate-200 bg-white rounded-md px-3 py-2 mb-3"
      data-testid="myob-health-strip"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className={`inline-block h-2 w-2 rounded-full ${overallDot}`} data-testid="myob-health-overall-dot" />
          <span className="text-xs font-medium text-slate-700">MYOB Sync Health</span>
          {compact ? null : (
            <span className="text-[11px] text-slate-500">
              green &lt; 4h · amber &lt; 48h · red stale/errored
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={load}
          className="text-[11px] text-slate-500 hover:text-slate-900 flex items-center gap-1"
          data-testid="myob-health-refresh"
          title="Refresh health"
        >
          <RefreshCcw className="h-3 w-3" /> Refresh
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
        {(data.sources || []).map((s) => (
          <HealthChip key={s.kind} source={s} />
        ))}
      </div>
    </div>
  );
}

const STATUS_COLOR = {
  green: "bg-emerald-500",
  amber: "bg-amber-500",
  red: "bg-rose-500",
};

const STATUS_ICON = {
  green: CircleCheck,
  amber: CircleAlert,
  red: CircleAlert,
};

function HealthChip({ source }) {
  const Icon = STATUS_ICON[source.status] || CircleDashed;
  const dot = STATUS_COLOR[source.status] || "bg-slate-400";
  const tooltip = buildTooltip(source);
  return (
    <div
      className="flex items-center gap-2 border border-slate-200 rounded px-2 py-1.5 text-xs"
      title={tooltip}
      data-testid={`myob-health-chip-${source.kind}`}
    >
      <span className={`inline-block h-2 w-2 rounded-full ${dot} shrink-0`} />
      <div className="min-w-0 flex-1">
        <div className="font-medium text-slate-800 truncate">{source.label}</div>
        <div className="text-[10px] text-slate-500 flex items-center gap-1">
          {source.status === "green" && <span>Fresh · {source.row_count.toLocaleString()} rows</span>}
          {source.status === "amber" && <span>{formatHours(source.hours_since_success)} · {source.row_count.toLocaleString()} rows</span>}
          {source.status === "red" && (
            <span>{source.last_success_at ? `${formatHours(source.hours_since_success)} stale` : "Never synced"}</span>
          )}
        </div>
      </div>
      {source.status !== "green" && (
        <Icon
          className={`h-3.5 w-3.5 shrink-0 ${source.status === "red" ? "text-rose-500" : "text-amber-500"}`}
        />
      )}
    </div>
  );
}

function formatHours(h) {
  if (h === null || h === undefined) return "—";
  if (h < 1) return `${Math.round(h * 60)}m ago`;
  if (h < 24) return `${h.toFixed(1)}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

function buildTooltip(s) {
  const lines = [];
  lines.push(s.label);
  lines.push(`Status: ${s.status.toUpperCase()}`);
  if (s.last_success_at) lines.push(`Last success: ${new Date(s.last_success_at).toLocaleString()}`);
  else lines.push("No successful sync recorded");
  if (s.last_run_status === "error" && s.last_error) {
    lines.push(`Last run ERRORED: ${String(s.last_error).slice(0, 200)}`);
  }
  lines.push(`Rows in DB: ${s.row_count.toLocaleString()}`);
  return lines.join("\n");
}
